import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import {
  NavigationEnd,
  NavigationError,
  PRIMARY_OUTLET,
  ResolveEnd,
  ResolveStart,
  Router,
  UrlSegment,
  UrlSegmentGroup,
  UrlTree,
} from '@angular/router';
import { filter, take } from 'rxjs/operators';
import { NavigationDataService } from './navigation-data.service';
import { IParkTurbineData, NavigationDataModel } from '../../../models/navigation-data.model';
import * as _ from 'lodash';
import { NavigationLevel, PageNavigationItem } from './navigation.interface';
import { CustomNavigationTitleService } from '../../custom-nav-title/custom-nav-title.service';
import { LoadNavigationDataService } from './load-navigation-data.service';
import { DefaultModuleService } from './default-module.service';
import { LocalStorageService } from '../../local-storage-service/local-storage.service';
import { DimensionService } from 'app/analyze/service/dimension-service/dimension.service';
import { GlobalErrorHandler } from 'app/utils/error-handler';
import { UserPortfolioPreferencesService } from '../../user-preferences/user-portfolio-preferences.service';
import { UserProfileService } from 'app/shared/user-profile/user-profile.service';

export enum NavigationRedirects {
  ModuleRedirect = 0,
}

export enum NavigationCategory {
  Module = 'MODULE',
  Level = 'LEVEL',
  PortfolioLevelSelectedPortfolio = 'SELECTED_PORTFOLIO',
  PortfolioLevelSelectedParks = 'SELECTED_PARKS',
  ParkLevelSelectedPark = 'SELECTED_PARK',
  ParkLevelSelectedTurbines = 'SELECTED_TURBINES',
  TurbineLevelSelectedTurbine = 'SELECTED_TURBINE',
}

interface Dictionary<T> {
  [Key: string]: T;
}

@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  private navigationDetails: NavigationDetails = new NavigationDetails('portfolio');

  private moduleId = this.defaultModuleService.getDefaultModule();
  private portfolioSubmoduleId = null;
  private parkSubmoduleId = null;
  private turbineSubmoduleId = null;
  private level: NavigationLevel = 'portfolio';

  private showModuleMap: Map<string, boolean> = new Map<string, boolean>();
  private lockModuleMap: Map<string[], boolean> = new Map<string[], boolean>();

  private basicModules = [
    'module_monitor_windlog',
    'module_analyze_windlog',
    'module_forecast_windlog',
    'module_maintain_windlog',
    'module_troubleshoot_windlog',
    'module_budget_windlog',
    'module_datastudio_windlog',
    'module_reporting_windlog',
  ];

  private navigationSubscribers: Dictionary<Array<ReplaySubject<NavigationDetails>>> = {};
  private newEventMode = false;
  private previousNavigationDetails: NavigationDetails;

  public modulesWithoutNavigation = ['data_studio', 'settings', 'pexapark', 'check-ppa', 'reporting/detail'];
  public modulesWithoutTurbineLevel = [
    'forecast',
    'reporting',
    'performance-analytics',
    'check_revenues',
    'budget',
    'revenues',
  ];
  allNavigationData: NavigationDataModel;

  constructor(
    private router: Router,
    private userProfileService: UserProfileService,
    private navigationDataService: NavigationDataService,
    private userPortfolioService: UserPortfolioPreferencesService,
    private customNavigationTitleService: CustomNavigationTitleService,
    private loadNavigationDataService: LoadNavigationDataService,
    private defaultModuleService: DefaultModuleService,
    private localStorageService: LocalStorageService,
    private dismensionService: DimensionService,
    private globalErrorHandler: GlobalErrorHandler
  ) {
    this.subscribeToRouteChange();
  }

  private isInNewPlanEvent = new BehaviorSubject<any>(null);

  public setIsInNewPlanEvent(value) {
    this.newEventMode = value;
    this.isInNewPlanEvent.next(value);
  }

  public getIsInNewPlanEvent(): Observable<any> {
    return this.isInNewPlanEvent.asObservable();
  }

  public getNavigationObservable(
    navigationCategory: NavigationCategory | NavigationCategory[],
  ): ReplaySubject<NavigationDetails> {
    const categories = !Array.isArray(navigationCategory) ? [navigationCategory] : navigationCategory;
    const newObservable$ = new ReplaySubject<NavigationDetails>(1);
    categories.forEach(categoriesElem => {
      if (!this.navigationSubscribers[categoriesElem.toString()]) {
        this.navigationSubscribers[categoriesElem.toString()] = [newObservable$];
      } else {
        this.navigationSubscribers[categoriesElem.toString()].push(newObservable$);
      }
    });
    if (!this.navigationDetails.isEmpty()) {
      newObservable$.next(this.navigationDetails);
    }
    return newObservable$;
  }

  private dispatchSubscribers(navigationCategory: NavigationCategory[]) {
    let collectedSubscribers = new Set<ReplaySubject<NavigationDetails>>();
    navigationCategory.forEach(navigationCategoryKey => {
      if (navigationCategoryKey !== null) {
        let obsList = this.navigationSubscribers[navigationCategoryKey.toString()];
        if (obsList) {
          obsList = obsList.filter(s => {
            return s.observers.length > 0;
          });
          obsList.forEach(obsElem => {
            collectedSubscribers.add(obsElem);
          });
        }
      }
    });

    if (collectedSubscribers) {
      collectedSubscribers.forEach(sub => {
        sub.next(this.navigationDetails);
      });
    }
  }

  // --------------------------- LOCKING, DISABLING & REDIRECTING METHODS ------------------------------

  public isLocked(roles: string[]): boolean {
    if (this.lockModuleMap.has(roles)) {
      return this.lockModuleMap.get(roles);
    }
    const result = !this.userProfileService.canAccessToModule(roles?.[0]);
    this.lockModuleMap.set(roles, result);
    return result;
  }

  public showModule(module: string): boolean {
    if (this.showModuleMap.has(module)) {
      return this.showModuleMap.get(module);
    }
    let result = this.userProfileService.canAccess([module]);
    if (!result) {
      const index = this.basicModules.findIndex(item => item === module);
      result = index > -1 && this.userProfileService.canAccess(['demo_user']);
    }
    this.showModuleMap.set(module, result);
    return result;
  }

  public isModuleActive(routeModule: string): boolean {
    return routeModule === this.navigationDetails.module;
  }

  public isLevelActive(routeLevel: string): boolean {
    return routeLevel === this.navigationDetails.level;
  }

  public isNavigationLevelActivated(level: string, module: string) {
    return ModuleRedirectHelper.isNavigationLevelActivated(level, module);
  }

  public isCountryDeactivated(code: string) {
    return ModuleRedirectHelper.isCountryDeactivatedForModule(this.navigationDetails.module, code);
  }

  public isParkDeactivated(parkId: string) {
    const country = this.navigationDataService.getParkCountryById(parkId);
    return this.navigationDetails.module
      ? ModuleRedirectHelper.isParkDeactivatedForModule(this.navigationDetails.module, parkId, country)
      : false;
  }

  public isTurbineDeactivated(parkId: string) {
    return ModuleRedirectHelper.isTurbineDeactivatedForModule(this.navigationDetails.module, parkId);
  }

  public showSelectTurbinesButton() {
    return ModuleRedirectHelper.isModuleSupportingTurbineSelection(this.navigationDetails.module);
  }

  // --------------------------- ROUTING METHODS ------------------------------

  public redirectToPortfolio(portfolioId: string, parkIds: string[] = [], portfolioName?: string, e?) {
    if (e) {
      e.preventDefault();
    }
    if (!this.newEventMode) {
      const nextParkIds = parkIds?.length ? parkIds : this.navigationDetails.selectedParksIds;
      this.setPortfolioId({ module: this.moduleId, level: 'portfolio' }, portfolioId, nextParkIds, true);
    }
    // this.setPortfolioId({module: this.moduleId, level: 'portfolio'}, portfolioId, parkIds || this.navigationDetails.selectedParksIds, true);

    this.userPortfolioService.changePortfolioId(portfolioId);

    setTimeout(() => {
      const newRoute = this.getHrefLevel('portfolio');
      this.router.navigate([newRoute]);
    }, 100);
  }

  public redirectToPowerplant(powerplantId: string, turbineIds: string[], powerplantName?: string, e?) {
    if (e) {
      e.preventDefault();
    }

    if (this.moduleId === 'check_revenues' || this.moduleId === 'revenues') {
      const newPark = ModuleRedirectHelper.findFirstParkWithEnergyMeter(
        powerplantId,
        this.navigationDataService.getSubPortfolioListData(),
        this.navigationDetails.selectedPortfolioId,
        parkId => this.isParkDeactivated(parkId),
      );
      this.setParkId({ module: this.moduleId, level: this.level }, newPark, null);
    } else {
      this.setParkId({ module: this.moduleId, level: this.level }, powerplantId, turbineIds);
    }

    setTimeout(() => {
      const newRoute = this.getHrefLevel('park');
      this.router.navigate([newRoute]);
    }, 100);
  }

  public redirectToTurbine(turbineId: string, turbineName?: string, e?) {
    if (e) {
      e.preventDefault();
    }

    if (this.moduleId === 'de_ice' && !ModuleRedirectHelper.isActiveTurbine(this.moduleId, turbineId)) {
      const newTurbine = ModuleRedirectHelper.findFirstActiveTurbine(this.moduleId);
      this.setTurbineId({ module: this.moduleId, level: this.level }, newTurbine);
    } else {
      this.setTurbineId({ module: this.moduleId, level: this.level }, turbineId);
    }

    setTimeout(() => {
      const newRoute = this.getHrefLevel('turbine');
      this.router.navigate([newRoute]);
    }, 100);
  }

  public revertNavigation() {
    this.navigationDetails = _.cloneDeep(this.previousNavigationDetails);
    this.dispatchSubscribers([NavigationCategory.Module]);
    // this.level = this.navigationDetails.level;
    // this.navigateToModule(this.navigationDetails.module);
  }

  public navigateToModule(moduleId: string, e?): void {
    if (e) {
      e.preventDefault();
    }
    if (this.navigationDetails.isEmpty()) {
      return;
    }

    if (this.newEventMode) {
      this.previousNavigationDetails = _.cloneDeep(this.navigationDetails);
    }

    this.setModuleId(moduleId);

    let newLevel;
    if (!ModuleRedirectHelper.isNavigationLevelActivated(this.level, moduleId)) {
      newLevel = ModuleRedirectHelper.getRedirectNavigationLevel(this.level, moduleId);
    } else {
      newLevel = this.navigationDetails.level;
    }

    if (moduleId === 'de_ice') {
      newLevel = 'turbine';
      const defaultPortfolioId = this.navigationDataService.getSubPortfolioListData().value.portfolios[0].id;
      const defaultParks = this.navigationDataService
        .getSubPortfolioListData()
        .value.portfolios[0].powerplants.filter(park => !this.isParkDeactivated(park.id));
      this.setPortfolioId(
        { module: moduleId, level: newLevel },
        defaultPortfolioId,
        defaultParks.map(p => p.id),
        true,
      );
      this.userPortfolioService.changePortfolioId(defaultPortfolioId);
      this.changeNavigationDetails('selectedTurbineId', '5ce41edd09d9fa07a43496b0'); //id of Juvent 5
      this.changeNavigationDetails('selectedParkId', '5cd44b536ac2c13f04b23cce'); //id of Juvent
    } else if (moduleId === 'check_revenues' || moduleId === 'revenues') {
      const defaultPortfolioId = this.navigationDataService.getSubPortfolioListData().value.portfolios[0].id;
      const powerPlants = this.navigationDataService
        .getSubPortfolioListData()
        .value.portfolios.find(
          p => p.id === (this.navigationDetails?.selectedPortfolioId ?? defaultPortfolioId),
        ).powerplants;
      this.navigationDetails.selectedParksIds = powerPlants
        .filter(p => p.energyMeterAvailable === true)
        .map(p => p.id)
        .filter(parkId => !this.isParkDeactivated(parkId));
      this.changeNavigationDetails('selectedParksIds', this.navigationDetails.selectedParksIds);
    } else {
      // check for deactivated parks & turbines when switching module:
      this.changeNavigationDetails(
        'selectedParksIds',
        this.navigationDetails.selectedParksIds.filter(parkId => !this.isParkDeactivated(parkId)),
      );
      this.changeNavigationDetails(
        'selectedTurbineIds',
        this.navigationDetails.selectedTurbineIds
          ? this.navigationDetails.selectedTurbineIds.filter(turbineId => !this.isTurbineDeactivated(turbineId))
          : [],
      );
    }
    if (newLevel) {
      switch (newLevel) {
        case 'portfolio': {
          this.redirectToPortfolio(
            this.navigationDetails.selectedPortfolioId || this.userPortfolioService.getPortfolioId(),
            this.navigationDetails.selectedParksIds.filter(parkId => !this.isParkDeactivated(parkId)),
          );
          break;
        }
        case 'park': {
          this.redirectToPowerplant(this.navigationDetails.selectedParkId, this.navigationDetails.selectedTurbineIds);
          break;
        }
        case 'turbine': {
          this.redirectToTurbine(this.navigationDetails.selectedTurbineId);
          break;
        }
      }
    } else {
      // no level at all allowed for selected module:
      setTimeout(() => {
        const newRoute = this.getHrefLevel(null);
        this.router.navigate([newRoute]);
      }, 100);
    }
  }

  public operateRoute(link: any, e) {
    if (e) {
      e.preventDefault();
    }

    const href = this.getHrefForOperate(link);
    if (href) {
      window.open(href, '_blank');
    }
  }

  public setPageItems(items: PageNavigationItem[]) {
    this.navigationDetails.pageItems = items;
  }

  public getPageItems() {
    return this.navigationDetails.pageItems;
  }

  public getHrefForOperate(link: any) {
    if (link === 'inge') {
      return 'https://demos.ingeboards.com/users/login';
    } else if (!this.isLocked(['module_operate_windlog'])) {
      return 'https://monitor-bkw-staging.cluster01.quantec-systems.com/logout';
    }
    return null;
  }

  public getHrefRoute(routeModule: string): any {
    const parkName = this.navigationDataService.getParkNameForNavigationById(this.navigationDetails.selectedParkId);

    if (routeModule === 'overview') {
      return 'overview';
    }

    switch (this.navigationDetails.level) {
      case 'park':
        return routeModule + '/' + this.navigationDetails.level + '/' + parkName;
      case 'turbine':
        return (
          routeModule +
          '/' +
          this.navigationDetails.level +
          '/' +
          this.navigationDataService.getTurbineSerialForNavigationById(this.navigationDetails.selectedTurbineId)
        );
      case 'portfolio':
        return routeModule + '/portfolio';
      default: {
        return routeModule + '/';
      }
    }
  }

  public getHrefLevel(routeLevel: string): string {
    if (routeLevel === null) {
      return this.navigationDetails.module + '/';
    }

    let name = null;
    let submoduleId = null;
    switch (routeLevel) {
      case 'park':
        name = this.navigationDataService.getParkNameForNavigationById(this.navigationDetails.selectedParkId);
        submoduleId = this.parkSubmoduleId;
        break;
      case 'turbine':
        name = this.navigationDataService.getTurbineSerialForNavigationById(this.navigationDetails.selectedTurbineId);
        submoduleId = this.turbineSubmoduleId;
        break;
      default:
        submoduleId = this.portfolioSubmoduleId;
    }
    const modulesStoringPageItemsToNavigate = [
      'analyze',
      'budget',
      'forecast',
      'kpi-follow-up',
      'planning',
      'troubleshoot',
      'performance-analytics',
    ];
    const submodule = modulesStoringPageItemsToNavigate.includes(this.navigationDetails.module)
      ? (this.navigationDetails.pageItems || []).filter(i => i.active)[0]
      : {};

    if (this.navigationDetails.module === 'overview') return 'overview';

    return [this.navigationDetails.module, routeLevel, name, submoduleId || (submodule ? submodule['id'] : '')]
      .filter(Boolean)
      .join('/');
  }

  public handleCheckRevenuesParkMissing() {
    this.router.events.pipe(take(1)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        const splittedUrl = event.urlAfterRedirects.split('/');
        if (splittedUrl[1] === 'check_revenues' && splittedUrl[2] === 'park' && splittedUrl[3] === '') {
          this.router.navigate([
            '/check_revenues/park/' +
              this.navigationDataService.getParkNameForNavigationById(this.navigationDetails.selectedParkId),
          ]);
        }
      }
    });
  }
  public handleRevenuesParkMissing() {
    this.router.events.pipe(take(1)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        const splittedUrl = event.urlAfterRedirects.split('/');
        if (splittedUrl[1] === 'revenues' && splittedUrl[2] === 'park' && splittedUrl[3] === '') {
          this.router.navigate([
            '/revenues/park/' +
              this.navigationDataService.getParkNameForNavigationById(this.navigationDetails.selectedParkId),
          ]);
        }
      }
    });
  }

  public setSelectedParksList(selectedParks: string[], emitValue: boolean = false) {
    const changed = this.changeNavigationDetails('selectedParksIds', selectedParks);
    if (emitValue && changed) {
      this.dispatchSubscribers([NavigationCategory.PortfolioLevelSelectedParks]);
    }
  }

  public setSelectedTurbinesList(selectedTurbines: string[]) {
    const changed = this.changeNavigationDetails('selectedTurbineIds', selectedTurbines);
    if (changed) {
      this.dispatchSubscribers([NavigationCategory.ParkLevelSelectedTurbines]);
    }
  }

  // --------------------------- PRIVATE METHODS ------------------------------

  private changeNavigationDetails(property: string, newValue: any): boolean {
    // navigationDetails needs to be changed like this in order to make rxjs distinctUntilChanged work!
    if (JSON.stringify(this.navigationDetails[property]) !== JSON.stringify(newValue)) {
      const clone = _.cloneDeep(this.navigationDetails);
      clone[property] = newValue;
      clone['_lastChanged'] = property;
      this.navigationDetails = clone;
      return true;
    } else {
      return false;
    }
  }

  public setLevel(level): void {
    this.level = level;

    const changed = this.changeNavigationDetails('level', level);
    if (changed) {
      this.dispatchSubscribers([NavigationCategory.Level]);
    }
  }

  private setPortfolioId(
    navigationDetails: { module: string; level: any },
    portfolioId,
    parkIds: string[],
    emit = true,
  ) {
    const changed1 = this.changeNavigationDetails('level', 'portfolio');
    const changed2 = this.changeNavigationDetails('selectedPortfolioId', portfolioId);

    const changed3 = this.changeNavigationDetails('selectedParksIds', parkIds);
    const changed4 = this.changeNavigationDetails('module', navigationDetails.module);

    this.moduleId = navigationDetails.module;
    this.level = navigationDetails.level;

    this.dispatchSubscribers([
      changed1 ? NavigationCategory.Level : null,
      changed2 ? NavigationCategory.PortfolioLevelSelectedPortfolio : null,
      changed3 ? NavigationCategory.PortfolioLevelSelectedParks : null,
      changed4 ? NavigationCategory.Module : null,
    ]);
  }

  private setParkId(navigationDetails: { module: string; level: any }, parkId: string, turbineIds?: string[]): void {
    const changed1 = this.changeNavigationDetails('level', 'park');
    const changed2 = this.changeNavigationDetails('selectedParkId', parkId);
    const changed3 = this.changeNavigationDetails('selectedTurbineIds', turbineIds);

    // const changed4 = this.changeNavigationDetails('level', navigationDetails.level)
    const changed5 = this.changeNavigationDetails('module', navigationDetails.module);

    this.moduleId = navigationDetails.module;
    this.level = navigationDetails.level;

    this.dispatchSubscribers([
      changed1 ? NavigationCategory.Level : null,
      changed2 ? NavigationCategory.ParkLevelSelectedPark : null,
      changed3 ? NavigationCategory.ParkLevelSelectedTurbines : null,
      changed5 ? NavigationCategory.Module : null,
    ]);
  }

  private setTurbineId(navigationDetails: { module: string; level: any }, turbineId: string): void {
    const park = this.allNavigationData?.powerplants?.find(p => p.turbines?.find(t => t.id === turbineId))?.id;
    if (park && this.navigationDetails.selectedParkId !== park) {
      this.navigationDetails.selectedParkId = park;
    }
    const changed1 = this.changeNavigationDetails('level', 'turbine');
    const changed2 = this.changeNavigationDetails('selectedTurbineId', turbineId);

    // const changed3 = this.changeNavigationDetails('level', navigationDetails.level)
    const changed4 = this.changeNavigationDetails('module', navigationDetails.module);

    this.moduleId = navigationDetails.module;
    this.level = navigationDetails.level;

    this.dispatchSubscribers([
      changed1 ? NavigationCategory.Level : null,
      changed2 ? NavigationCategory.TurbineLevelSelectedTurbine : null,
      changed4 ? NavigationCategory.Module : null,
    ]);
  }

  public setModuleId(moduleId: string) {
    this.turbineSubmoduleId = null;

    /* Clear url parameters (not query params) in case of switching modules */
    if (this.moduleId !== moduleId) {
      this.parkSubmoduleId = '';
      this.portfolioSubmoduleId = '';
    }

    this.moduleId = moduleId;
    this.setPageItems([]);

    const changed = this.changeNavigationDetails('module', moduleId);
    if (changed) {
      this.dispatchSubscribers([NavigationCategory.Module]);
    }
  }

  private subscribeToRouteChange(): void {
    this.router.events.subscribe(val => {
      if (val instanceof ResolveStart) {
        this.loadNavigationDataService.startLoadingTimer();
      }
      if (val instanceof ResolveEnd) {
        this.loadNavigationDataService.endLoadingTimer();
      }
      this.customNavigationTitleService.clearTitle();
      if (val instanceof NavigationEnd) {
        const segments = this.getUrlSegments(val.url);
        if (segments[0] === 'reporting' && segments[1] === 'detail') segments[0] = 'reporting/detail';
        if (this.navigationDetails.isEmpty()) {
          setTimeout(() => {
            this.extractDimensions(segments);
          }, 1000);
        } else {
          this.extractDimensions(segments);
        }
      } else if (val instanceof NavigationError) {
        if (
          val?.error?.name === 'ChunkLoadError' ||
          val?.error?.message?.includes('Failed to fetch dynamically imported module')
        ) {
          this.globalErrorHandler.handleAppVersionError(val);
        } else {
          const defaultModule = this.defaultModuleService.getDefaultModule();
          this.globalErrorHandler.handleErrorManually(val);
          this.router.navigate([defaultModule]);
        }
      }
    });
  }

  private extractDimensions(segments: any): void {
    this.portfolioSubmoduleId = null;
    this.parkSubmoduleId = null;
    this.turbineSubmoduleId = null;

    const navigationObject = {
      module: '',
      level: '',
    };

    if (segments.length > 0 && segments[0] !== null) {
      /* We check if the module is included in the module list */
      if (ModuleRedirectHelper.getModuleList().includes(segments[0])) {
        /* If all properties of the module object (portfolio, park, park_level_supports_turbine_selection and turbine)
          are false, means the module does not have level selection, so we just set the module */
        if (Object.values(ModuleRedirectHelper.getModuleObject(segments[0])).every(v => !v)) {
          this.setModuleId(segments[0]);
          return;
        }
      }
      /* In case the url has a different module than the available ones, we redirect to default module */
      navigationObject.module = ModuleRedirectHelper.getModuleList().includes(segments[0])
        ? segments[0]
        : this.defaultModuleService.getDefaultModule();
    }

    if (
      segments.length > 1 &&
      segments[1] !== null &&
      segments.length > 0 &&
      segments[0] !== null &&
      segments[1] !== 'portfolio' &&
      segments[1] !== 'detail'
    ) {
      // this.setLevel(segments[1]);
      navigationObject.level = segments[1];
    }

    if (this.modulesWithoutTurbineLevel.includes(navigationObject.module) && segments[1] === 'turbine') {
      const turbine = this.dismensionService.turbineList.find(item => item.id_device === Number(segments[2]));
      const selectedParkId = turbine?.id_windpark;
      const parkName = turbine?.parkName;
      const turbineIds = this.dismensionService.turbineList
        .filter(item => item.parkName === parkName)
        .map(item => item._id);
      selectedParkId && this.redirectToPowerplant(selectedParkId, turbineIds);
    }

    this.navigationDataService
      .getSubPortfolioListData()
      .pipe(
        take(1),
        filter(d => !!d),
      )
      .subscribe(data => {
        this.allNavigationData = data;
        const portfolioIndex = segments.findIndex((seg: string) => seg === 'portfolio');
        const plantIndex = segments.findIndex((seg: string) => seg === 'park');
        const turbineIndex = segments.findIndex((seg: string) => seg === 'turbine');

        if (portfolioIndex > -1 || (plantIndex < 0 && turbineIndex < 0)) {
          if (segments[0]) {
            this.portfolioSubmoduleId = segments[portfolioIndex + 1];
          } else {
            this.redirectToPortfolio(
              data.selectedPortfolio.id,
              data.selectedPortfolio.powerplants.map(p => p.id),
            );
          }
          navigationObject.level = 'portfolio';
          if (this.navigationDetails.selectedPortfolioId) {
            this.setPortfolioId(
              navigationObject,
              this.navigationDetails.selectedPortfolioId,
              this.navigationDetails.selectedParksIds,
            );
          } else {
            const portfolioIdfromLocalStorage = this.localStorageService.getSelectedPortfolioId();
            let parkIds = [];
            if (portfolioIdfromLocalStorage) {
              parkIds = data.portfolios.find(p => p.id === portfolioIdfromLocalStorage).powerplants;
            } else {
              parkIds = data.portfolios[0].powerplants;
            }
            // temporal solution for check_revenues module, ideally do this filter in isParkDeactivated
            if (navigationObject.module === 'check_revenues' || navigationObject.module === 'revenues')
              parkIds = parkIds.filter(p => p.energyMeterAvailable === true);
            this.setPortfolioId(
              navigationObject,
              portfolioIdfromLocalStorage || data.portfolios[0].id,
              parkIds.map(p => p.id),
            );
          }
        } else if (plantIndex > -1) {
          if (segments[plantIndex + 1]) {
            const powerPlant = data.powerplants.find(p => p.nameLong === segments[plantIndex + 1]);
            this.parkSubmoduleId = segments.length > plantIndex + 2 ? segments[plantIndex + 2] : null;
            // if there was a turbine selection before, try to restore it:
            if (
              !this.navigationDetails.selectedTurbineIds?.every(v => powerPlant.turbines.map(p => p.id).includes(v)) ||
              this.navigationDetails.selectedTurbineIds.length !== powerPlant.turbines.length
            ) {
              if (powerPlant.id !== this.navigationDetails.selectedParkId) {
                this.setParkId(
                  navigationObject,
                  powerPlant.id || segments[plantIndex + 1],
                  powerPlant.turbines.map(p => p.id),
                );
              } else {
                this.setParkId(
                  navigationObject,
                  powerPlant.id || segments[plantIndex + 1],
                  this.navigationDetails.selectedTurbineIds || powerPlant.turbines.map(p => p.id),
                );
              }
            } else {
              this.setParkId(
                navigationObject,
                powerPlant.id || segments[plantIndex + 1],
                powerPlant.turbines[0] ? powerPlant.turbines.map(inv => inv.id) : null,
              );
            }
          }
        } else if (turbineIndex > -1) {
          if (segments[turbineIndex + 1]) {
            let turbine = null;
            this.turbineSubmoduleId = segments.length > turbineIndex + 2 ? segments[turbineIndex + 2] : null;
            data.powerplants.forEach((p: IParkTurbineData) => {
              if (!turbine) {
                turbine = p.turbines.find(m => {
                  return String(m.serialNav) === String(segments[turbineIndex + 1]);
                });
              }
            });

            this.setTurbineId(
              navigationObject,
              (turbine && turbine.id) || segments[turbineIndex + 1] || data[0].turbines[0].id,
            );
          }
        } else if (
          !this.navigationDetails.selectedTurbineId &&
          !this.navigationDetails.selectedParkId &&
          !segments[0]?.includes('reporting')
        ) {
          if (data.selectedPortfolio) {
            this.redirectToPortfolio(
              data.selectedPortfolio.id,
              data.selectedPortfolio.powerplants.map(p => p.id),
            );
          } else {
            this.redirectToPortfolio(
              data.portfolios[0].id,
              data.portfolios[0].powerplants.map(p => p.id),
            );
          }
        } else if (segments[0]?.includes('reporting')) this.setModuleId(segments[0]);
      });
  }

  private getUrlSegments(url: string) {
    const tree: UrlTree = this.router.parseUrl(url);
    const g: UrlSegmentGroup = tree.root.children[PRIMARY_OUTLET];
    const s: UrlSegment[] = g ? g.segments : [];
    return s.map(segment => segment.path);
  }
}

// ------------------------------------------------------------- //

class ModuleRedirectHelper {
  private static navigationModuleLevelMapping = {
    overview: {
      portfolio: true,
      park: false,
      park_level_supports_turbine_selection: false,
      turbine: false,
    },
    monitor: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: true,
    },
    analyze: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: true,
    },
    forecast: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: false,
    },
    planning: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: true,
    },
    troubleshoot: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: true,
    },
    budget: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: false,
      turbine: false,
    },
    data_studio: {
      portfolio: false,
      park: false,
      park_level_supports_turbine_selection: false,
      turbine: false,
    },
    reporting: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: false,
      turbine: false,
    },
    'reporting/detail': {
      portfolio: false,
      park: false,
      park_level_supports_turbine_selection: false,
      turbine: false,
    },
    check_revenues: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: false,
      turbine: false,
      deactivated_elements: {},
    },
    revenues: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: false,
      turbine: false,
      deactivated_elements: {},
    },
    de_ice: {
      portfolio: false,
      park: false,
      park_level_supports_turbine_selection: false,
      turbine: true,
      activated_elements: {
        parks: ['5cd44b536ac2c13f04b23cce'] /* JUV */,
        turbines: [
          '5ce41edd09d9fa07a43496ac' /* JUV 1*/,
          '5ce41edd09d9fa07a43496ad' /* JUV 2*/,
          '5ce41edd09d9fa07a43496ae' /* JUV 3*/,
          '5ce41edd09d9fa07a43496af' /* JUV 4*/,
          '5ce41edd09d9fa07a43496b0' /* JUV 5*/,
          '5ce41edd09d9fa07a43496b1' /* JUV 6*/,
          '5ce41edd09d9fa07a43496b2' /* JUV 7*/,
          '5ce41edd09d9fa07a43496b3' /* JUV 8*/,
          '5ce41edd09d9fa07a434965e' /* JUV 9*/,
          '5ce41edd09d9fa07a434965d' /* JUV 10*/,
          '5ce41edd09d9fa07a434965b' /* JUV 11*/,
          '5ce41edd09d9fa07a434965c' /* JUV 12*/,
          '5ce41edd09d9fa07a434965a' /* JUV 13*/,
          '5ce41edd09d9fa07a4349661' /* JUV 14*/,
          '5ce41edd09d9fa07a4349660' /* JUV 15*/,
          '5ce41edd09d9fa07a434965f' /* JUV 16*/,
        ],
      },
    },
    logbook: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: true,
    },
    'kpi-follow-up': {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: true,
    },
    pexapark: {
      portfolio: false,
      park: false,
      park_level_supports_turbine_selection: false,
      turbine: false,
    },
    'check-ppa': {
      portfolio: false,
      park: false,
      park_level_supports_turbine_selection: false,
      turbine: false,
    },
    predictive: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: true,
    },
    'performance-analytics': {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: false,
    },
    tickets: {
      portfolio: true,
      park: true,
      park_level_supports_turbine_selection: true,
      turbine: true,
    },
    settings: {
      portfolio: false,
      park: false,
      park_level_supports_turbine_selection: false,
      turbine: false,
    },
  };

  static isNavigationLevelActivated(level: string, module: string) {
    return !module ? false : this.navigationModuleLevelMapping[module][level];
  }

  static isModuleSupportingTurbineSelection(module: string) {
    return this.navigationModuleLevelMapping[module]['park_level_supports_turbine_selection'];
  }

  static isCountryDeactivatedForModule(module: string, countryCode: string) {
    const res =
      this.navigationModuleLevelMapping[module]['deactivated_elements'] &&
      this.navigationModuleLevelMapping[module]['deactivated_elements']['countries'] &&
      this.navigationModuleLevelMapping[module]['deactivated_elements']['countries'].indexOf(countryCode) > -1;
    return res;
  }

  static isParkDeactivatedForModule(module: string, parkId: string, countryCode: string) {
    // 1. check: is park deactivated because of country?
    const res =
      this.navigationModuleLevelMapping[module]['deactivated_elements'] &&
      this.navigationModuleLevelMapping[module]['deactivated_elements']['countries'] &&
      this.navigationModuleLevelMapping[module]['deactivated_elements']['countries'].indexOf(countryCode) > -1;
    if (res) {
      return true;
    } else {
      // 2. check: is park itself explicitely deactivated ?
      const res2 =
        this.navigationModuleLevelMapping[module]['deactivated_elements'] &&
        this.navigationModuleLevelMapping[module]['deactivated_elements']['parks'] &&
        this.navigationModuleLevelMapping[module]['deactivated_elements']['parks'].indexOf(parkId) > -1;
      if (res2) {
        return true;
      } else {
        // 3. check: list of exclusively activated parks ?
        const res3 =
          this.navigationModuleLevelMapping[module]['activated_elements'] &&
          this.navigationModuleLevelMapping[module]['activated_elements']['parks'] &&
          !(this.navigationModuleLevelMapping[module]['activated_elements']['parks'].indexOf(parkId) > -1);
        return res3;
      }
    }
  }

  static isTurbineDeactivatedForModule(module: string, turbineId: string) {
    // 1. check: is turbine itself explicitely deactivated ?
    const res1 =
      this.navigationModuleLevelMapping[module]['deactivated_elements'] &&
      this.navigationModuleLevelMapping[module]['deactivated_elements']['turbines'] &&
      this.navigationModuleLevelMapping[module]['deactivated_elements']['turbines'].indexOf(turbineId) > -1;
    if (res1) {
      return true;
    } else {
      // 2. check: list of exclusively activated turbines ?
      const res2 =
        this.navigationModuleLevelMapping[module]['activated_elements'] &&
        this.navigationModuleLevelMapping[module]['activated_elements']['turbines'] &&
        !(this.navigationModuleLevelMapping[module]['activated_elements']['turbines'].indexOf(turbineId) > -1);
      return res2;
    }
  }

  static getRedirectUrl(moduleId: string, newLevel: NavigationLevel, segments) {
    return (
      '/' +
      moduleId +
      '/' +
      this.getRedirectNavigationLevel(newLevel, moduleId) +
      (segments.length > 1 && segments[2] ? '/' + segments[2] : '') +
      (segments.length > 2 && segments[3] ? '/' + segments[3] : '')
    );
  }

  static getRedirectNavigationLevel(currentLevel: NavigationLevel, module: string): NavigationLevel | null {
    switch (currentLevel) {
      case 'turbine': {
        const isParkAllowed = this.navigationModuleLevelMapping[module]['park'];
        if (isParkAllowed) {
          return 'park';
        } else {
          const isPortfolioAllowed = this.navigationModuleLevelMapping[module]['portfolio'];
          if (isPortfolioAllowed) {
            return 'portfolio';
          } else {
            return null;
          }
        }
      }
      case 'park': {
        const isPortfolioAllowed = this.navigationModuleLevelMapping[module]['portfolio'];
        if (isPortfolioAllowed) {
          return 'portfolio';
        } else {
          const isTurbineAllowed = this.navigationModuleLevelMapping[module]['turbine'];
          if (isTurbineAllowed) {
            return 'turbine';
          } else {
            return null;
          }
        }
      }
      case 'portfolio': {
        const isParkAllowed = this.navigationModuleLevelMapping[module]['park'];
        if (isParkAllowed) {
          return 'park';
        } else {
          const isTurbineAllowed = this.navigationModuleLevelMapping[module]['turbine'];
          if (isTurbineAllowed) {
            return 'turbine';
          } else {
            return null;
          }
        }
      }
    }
  }

  static findFirstActiveTurbine(module): any {
    const res2 = this.navigationModuleLevelMapping[module]['activated_elements']['turbines'][0];
    return res2;
  }

  static isActiveTurbine(module, turbineId): boolean {
    return (
      this.navigationModuleLevelMapping[module]['activated_elements']['turbines'] &&
      this.navigationModuleLevelMapping[module]['activated_elements']['turbines'].indexOf(turbineId) > -1
    );
  }

  static findFirstParkWithEnergyMeter(
    parkId,
    portfolioParkData,
    portfolioId?: string,
    isParkDeactivated: (parkId: string) => boolean = () => false,
  ): any {
    if (!!portfolioParkData && !!portfolioParkData.value) {
      const parkTurbineList = portfolioParkData.value.powerplants;
      if (parkTurbineList) {
        const currentPark = parkTurbineList.filter(p => p.id === parkId)[0];
        if (currentPark) {
          if (!currentPark.energyMeterAvailable) {
            const portfolio = portfolioParkData.value.portfolios?.find(p => p.id === portfolioId);
            const powerplants = portfolio?.powerplants ?? [];
            const newParkInner = powerplants.find(p => p.energyMeterAvailable === true && !isParkDeactivated(p.id));
            if (!newParkInner) {
              return currentPark.id;
            } else {
              return newParkInner.id;
            }
          } else {
            return currentPark.id;
          }
        }
      }
    }

    return null;
  }

  static getModuleList(): string[] {
    return Object.keys(this.navigationModuleLevelMapping);
  }

  static getModuleObject(module: string): {} {
    return module ? this.navigationModuleLevelMapping[module] : this.navigationModuleLevelMapping;
  }
}

export class NavigationDetails {
  private _module: string;
  private _level: NavigationLevel;

  private _selectedPortfolioId: string = null;
  private _selectedParkId: string = null;
  private _selectedTurbineId: string = null;

  private _selectedParksIds: string[] = null;
  private _selectedTurbineIds: string[] = null;

  private _lastChanged: string = null;
  private _pageItems: PageNavigationItem[] = null;

  public constructor(level) {
    this._level = level;
  }

  public isEmpty() {
    const hasNavigation = Object.values(ModuleRedirectHelper.getModuleObject(this._module)).every(v => !v);
    return (
      this._selectedPortfolioId === null &&
      this._selectedParkId === null &&
      this.selectedTurbineId === null &&
      !hasNavigation
    );
  }

  get pageItems(): PageNavigationItem[] {
    return this._pageItems;
  }

  set pageItems(items: PageNavigationItem[]) {
    this._pageItems = items;
  }

  get lastChanged(): string {
    return this._lastChanged;
  }

  get module(): string {
    return this._module;
  }

  set module(value: string) {
    this._module = value;
  }

  get level(): 'portfolio' | 'park' | 'turbine' {
    return this._level;
  }

  set level(value: 'portfolio' | 'park' | 'turbine') {
    this._level = value;
  }

  get selectedPortfolioId(): string {
    return this._selectedPortfolioId;
  }

  set selectedPortfolioId(value: string) {
    this._selectedPortfolioId = value;
  }

  get selectedParkId(): string {
    return this._selectedParkId;
  }

  set selectedParkId(value: string) {
    this._selectedParkId = value;
  }

  get selectedTurbineId(): string {
    return this._selectedTurbineId;
  }

  set selectedTurbineId(value: string) {
    this._selectedTurbineId = value;
  }

  get selectedParksIds(): string[] {
    return this._selectedParksIds;
  }

  set selectedParksIds(value: string[]) {
    this._selectedParksIds = value;
  }

  get selectedTurbineIds(): string[] {
    return this._selectedTurbineIds;
  }

  set selectedTurbineIds(value: string[]) {
    this._selectedTurbineIds = value;
  }
}
